/**
 * Picture-hanging algorithms for the palace. Pure data, no three.js.
 *
 * The centerpiece is the organic gallery-wall cluster: an anchor piece plus
 * frames packed around it by adjacency with one uniform gutter, the way a
 * curated gallery wall is laid out — edges align to neighbours, the composite
 * stays compact and roughly rectangular, and the whole cluster centres on the
 * wall around the eye line. Segments can instead ask for a single eye-level
 * row ('row') or one hero piece ('hero').
 *
 * Segments are world-space wall runs:
 *   { ox, oz, dx, dz, nx, nz, len, style, y0?, y1? }
 * with (ox,oz) the start of the run on the wall face, (dx,dz) the unit run
 * direction, and (nx,nz) the inward normal. Slots come back ready for
 * palaceBuild: { tweet, w, h, x, y, z, rotY }.
 */

import { PALACE, SIDES, clamp } from './constants';

const rectsClash = (a, b, gap) =>
  !(a.x + a.w / 2 + gap <= b.x - b.w / 2 ||
    b.x + b.w / 2 + gap <= a.x - a.w / 2 ||
    a.y + a.h / 2 + gap <= b.y - b.h / 2 ||
    b.y + b.h / 2 + gap <= a.y - a.h / 2);

const bboxOf = (items) => {
  let x1 = Infinity, x2 = -Infinity, y1 = Infinity, y2 = -Infinity;
  for (const it of items) {
    x1 = Math.min(x1, it.x - it.w / 2);
    x2 = Math.max(x2, it.x + it.w / 2);
    y1 = Math.min(y1, it.y - it.h / 2);
    y2 = Math.max(y2, it.y + it.h / 2);
  }
  return { x1, x2, y1, y2, w: x2 - x1, h: y2 - y1 };
};

/**
 * Pack an organic cluster from the front of `pool` into a maxW×maxH box.
 * Mutates pool (removes placed frames). Returns { items, w, h } with item
 * offsets relative to the cluster centre, or null if nothing fits.
 */
export const organicCluster = (pool, maxW, maxH, rand) => {
  const g = PALACE.GUTTER;
  const LOOK = 16;
  if (!pool.length || maxW < 0.6 || maxH < 0.6) return null;

  // Anchor: the largest piece near the front of the queue that fits.
  let anchorIdx = -1;
  let bestArea = 0;
  for (let i = 0; i < Math.min(LOOK, pool.length); i++) {
    const f = pool[i];
    if (f.w <= maxW && f.h <= maxH && f.w * f.h > bestArea) {
      bestArea = f.w * f.h;
      anchorIdx = i;
    }
  }
  if (anchorIdx < 0) return null;
  const items = [{ ...pool[anchorIdx], x: 0, y: 0 }];
  pool.splice(anchorIdx, 1);

  const maxCount = 4 + Math.floor(rand() * 9); // 4–12 pieces per cluster
  while (items.length < maxCount && pool.length) {
    let placed = false;
    for (let ci = 0; ci < Math.min(LOOK, pool.length) && !placed; ci++) {
      const c = pool[ci];
      if (c.w > maxW || c.h > maxH) continue;

      // Candidate positions: adjacent to each placed rect on each side, with
      // edge-aligned and centred variants — the vocabulary of a gallery wall.
      let best = null;
      for (const p of items) {
        const candidates = [];
        for (const sx of [1, -1]) {
          const x = p.x + sx * (p.w / 2 + g + c.w / 2);
          for (const y of [p.y + p.h / 2 - c.h / 2, p.y - p.h / 2 + c.h / 2, p.y]) {
            candidates.push({ x, y });
          }
        }
        for (const sy of [1, -1]) {
          const y = p.y + sy * (p.h / 2 + g + c.h / 2);
          for (const x of [p.x + p.w / 2 - c.w / 2, p.x - p.w / 2 + c.w / 2, p.x]) {
            candidates.push({ x, y });
          }
        }
        for (const pos of candidates) {
          const trial = { ...c, x: pos.x, y: pos.y };
          if (items.some((it) => rectsClash(trial, it, g - 0.01))) continue;
          const bb = bboxOf([...items, trial]);
          if (bb.w > maxW || bb.h > maxH) continue;
          const score = bb.w * bb.h + 1.5 * (Math.abs(pos.x) + Math.abs(pos.y));
          if (!best || score < best.score) best = { pos, score };
        }
      }
      if (best) {
        items.push({ ...c, x: best.pos.x, y: best.pos.y });
        pool.splice(ci, 1);
        placed = true;
      }
    }
    if (!placed) break;
    const bb = bboxOf(items);
    if (bb.w * bb.h > 0.72 * maxW * maxH) break; // dense enough — stop
  }

  // Recentre so the composite's bounding box sits on the origin.
  const bb = bboxOf(items);
  const cx = (bb.x1 + bb.x2) / 2;
  const cy = (bb.y1 + bb.y2) / 2;
  items.forEach((it) => { it.x -= cx; it.y -= cy; });
  return { items, w: bb.w, h: bb.h };
};

// Everything above packs the frame's *outer* rectangle, so two mouldings never
// touch however heavy they are. The slot that comes out the other side is the
// artwork opening again — the canvas is drawn to that, not to the moulding —
// recentred for the styles whose bottom rail is deeper than their top.
const toSlot = (seg, frame, along, y) => ({
  tweet: frame.tweet,
  style: frame.style,
  w: frame.aw,
  h: frame.ah,
  x: seg.ox + seg.dx * along + seg.nx * PALACE.WALL_OFF,
  z: seg.oz + seg.dz * along + seg.nz * PALACE.WALL_OFF,
  y: y - frame.offY,
  rotY: Math.atan2(seg.nx, seg.nz),
});

/** Hang one segment in its declared style. Mutates pool; returns slots. */
export const hangSegment = (seg, pool, rand) => {
  const { EYE, HANG_LO, RAIL } = PALACE;
  const y0 = seg.y0 ?? HANG_LO;
  const y1 = Math.min(seg.y1 ?? RAIL, RAIL);
  const slots = [];

  if (seg.style === 'hero') {
    // One centred piece — pick the largest that suits the wall.
    let idx = -1;
    let bestArea = 0;
    for (let i = 0; i < Math.min(20, pool.length); i++) {
      const f = pool[i];
      if (f.w <= seg.len * 0.75 && f.h <= y1 - y0 && f.w * f.h > bestArea) {
        bestArea = f.w * f.h;
        idx = i;
      }
    }
    if (idx >= 0) {
      const f = pool.splice(idx, 1)[0];
      slots.push(toSlot(seg, f, seg.len / 2, Math.max(EYE, y0 + f.h / 2)));
    }
    return slots;
  }

  if (seg.style === 'row') {
    // A single eye-level line hang, run centred on the segment.
    const GAP = 0.5;
    const row = [];
    let run = 0;
    for (let i = 0; i < pool.length && run < seg.len; ) {
      const f = pool[i];
      const next = run + (row.length ? GAP : 0) + f.w;
      if (f.h <= 2.3 && next <= seg.len) {
        row.push(f);
        pool.splice(i, 1);
        run = next;
      } else {
        i++;
      }
      if (seg.len - run < 0.6) break;
    }
    let x = (seg.len - run) / 2;
    for (const f of row) {
      slots.push(toSlot(seg, f, x + f.w / 2, EYE));
      x += f.w + GAP;
    }
    return slots;
  }

  // 'cluster' (default): one organic gallery wall, or two on a long run.
  const usable = seg.len - 0.6;
  const nClusters = usable > 10.5 ? 2 : 1;
  const spacing = 1.2;
  const cw = (usable - (nClusters - 1) * spacing) / nClusters;
  for (let k = 0; k < nClusters; k++) {
    const cluster = organicCluster(pool, cw, y1 - y0, rand);
    if (!cluster) continue;
    const centre = nClusters === 1
      ? seg.len / 2
      : seg.len / 2 + (k === 0 ? -1 : 1) * (cw + spacing) / 2;
    const cy = clamp(EYE + 0.35, y0 + cluster.h / 2, y1 - cluster.h / 2);
    for (const it of cluster.items) {
      slots.push(toSlot(seg, it, centre + it.x, cy + it.y));
    }
  }
  return slots;
};

/**
 * Hangable segments for a standard rectangular room: every interior face,
 * split around the doors (door span + casing + margin removed). `styles` maps
 * long/short walls to hang styles; door-flank stubs become heroes.
 *
 * opts: { longStyle, shortStyle, y1, doorPad, margin, skipSides }
 * `doorPad` widens the dead zone each door claims (a room whose doors carry
 * relief — a moon-gate ring, an aedicule — needs the wall around them left
 * bare); `skipSides` drops whole faces a room has furnished with something
 * else.
 */
export const rectSegments = (node, opts = {}) => {
  const { WALL_T, SEG_MARGIN, DOOR_W, RAIL } = PALACE;
  const xI1 = node.x1 + WALL_T;
  const xI2 = node.x2 - WALL_T;
  const zI1 = node.z1 + WALL_T;
  const zI2 = node.z2 - WALL_T;
  const w = xI2 - xI1;
  const d = zI2 - zI1;
  // Nothing climbs over the picture rail, whatever the room asked for.
  const y1 = Math.min(opts.y1 ?? RAIL, RAIL, node.height - 0.9);
  const longStyle = opts.longStyle ?? 'cluster';
  const shortStyle = opts.shortStyle ?? 'cluster';
  const doorPad = opts.doorPad ?? 0.55;
  const margin = opts.margin ?? SEG_MARGIN;
  const skip = new Set(opts.skipSides || []);

  const faces = [
    { side: 'N', ox: xI1, oz: zI2, dx: 1, dz: 0, len: w, isLong: w >= d },
    { side: 'S', ox: xI1, oz: zI1, dx: 1, dz: 0, len: w, isLong: w >= d },
    { side: 'E', ox: xI2, oz: zI1, dx: 0, dz: 1, len: d, isLong: d > w },
    { side: 'W', ox: xI1, oz: zI1, dx: 0, dz: 1, len: d, isLong: d > w },
  ];

  const segments = [];
  for (const f of faces) {
    if (skip.has(f.side)) continue;
    const n = SIDES[f.side]; // side normal points outward; hang normal is inward
    const nx = -n.dx;
    const nz = -n.dz;
    const doors = (node.doors || [])
      .filter((d0) => d0.side === f.side)
      .map((d0) => {
        const along = f.dx ? d0.at - f.ox : d0.at - f.oz;
        const half = (d0.w || DOOR_W) / 2 + doorPad;
        return [along - half, along + half];
      })
      .sort((a, b) => a[0] - b[0]);

    let cursor = margin;
    const runs = [];
    for (const [a, b] of doors) {
      if (a - cursor >= 1.2) runs.push([cursor, a]);
      cursor = Math.max(cursor, b);
    }
    if (f.len - margin - cursor >= 1.2) runs.push([cursor, f.len - margin]);

    for (const [a, b] of runs) {
      const len = b - a;
      const full = doors.length === 0;
      segments.push({
        ox: f.ox + f.dx * a,
        oz: f.oz + f.dz * a,
        dx: f.dx,
        dz: f.dz,
        nx,
        nz,
        len,
        y1,
        style: len < 3.2 ? 'hero' : full || f.isLong ? (f.isLong ? longStyle : shortStyle) : shortStyle,
      });
    }
  }
  // Longest walls first, so the showpiece clusters get the best pieces.
  return segments.sort((a, b) => b.len - a.len);
};

/**
 * One run on a named interior face, between two world coordinates along it.
 * For rooms that choose their own runs instead of taking whole walls — a
 * corridor hanging a single piece per bay between its pilasters, say.
 */
export const faceSegment = (node, side, a1, a2, opts = {}) => {
  const { WALL_T, RAIL } = PALACE;
  const n = SIDES[side];
  const horizontal = side === 'N' || side === 'S';
  const face = side === 'N' ? node.z2 - WALL_T
    : side === 'S' ? node.z1 + WALL_T
      : side === 'E' ? node.x2 - WALL_T : node.x1 + WALL_T;
  return {
    ox: horizontal ? a1 : face,
    oz: horizontal ? face : a1,
    dx: horizontal ? 1 : 0,
    dz: horizontal ? 0 : 1,
    nx: -n.dx,
    nz: -n.dz,
    len: a2 - a1,
    y1: Math.min(opts.y1 ?? RAIL, RAIL),
    style: opts.style ?? 'hero',
  };
};

/**
 * A run on an arbitrary wall line — the chamfers of an octagon, say. (nx, nz)
 * is the inward unit normal; the run is offset off the centreline by half a
 * wall so it lands on the face, matching `angledWall`'s convention.
 */
export const angledSegment = (ax, az, bx, bz, nx, nz, opts = {}) => {
  const { WALL_T, RAIL } = PALACE;
  const len = Math.hypot(bx - ax, bz - az);
  const off = opts.faceOffset ?? WALL_T / 2;
  return {
    ox: ax + nx * off,
    oz: az + nz * off,
    dx: (bx - ax) / len,
    dz: (bz - az) / len,
    nx,
    nz,
    len,
    y1: Math.min(opts.y1 ?? RAIL, RAIL),
    style: opts.style ?? 'hero',
  };
};
